import { useCallback, useEffect, useState } from "react";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getEventList } from "../api/event.api";
import type { EventListForAdminResponse } from "../types/event.types";

interface UseEventsReturn {
  events: EventListForAdminResponse[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number) => void;
  totalCount: number;
  refreshEvents: () => Promise<void>;
}

export const useEvents = (): UseEventsReturn => {
  const [events, setEvents] = useState<EventListForAdminResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hasInitialData, setHasInitialData] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getEventList({
        ...(debouncedSearch ? { keyword: debouncedSearch } : {}),
        index: page - 1,
        size: itemsPerPage,
      });

      setEvents([...(response.list || [])]);
      setTotalCount(response.paging?.total || 0);
      setHasInitialData(true);
    } catch (fetchError: unknown) {
      setError(getApiErrorMessage(fetchError, "Failed to load events"));
      setEvents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page]);

  const refreshEvents = async () => {
    await fetchEvents();
  };

  useEffect(() => {
    if (!hasInitialData) {
      fetchEvents();
    }
  }, [fetchEvents, hasInitialData]);

  useEffect(() => {
    if (hasInitialData) {
      fetchEvents();
    }
  }, [debouncedSearch, page]);

  return {
    events,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    totalCount,
    refreshEvents,
  };
};
